import { MainLayout } from "../components/layouts/MainLayout";
import React, { useContext, useEffect, useState } from "react";
import DateHelper from "../helpers/dateHelper";
import { MagnifyingGlassIcon, } from "@heroicons/react/24/solid";
import { AppMainStateContext } from "../contexts/AppMainStateContext";
import SearchInput from "../components/inputs/SearchInput";
import { useDebounce } from "../hooks/useDebounce";
import { useAppDispatch, useAppSelector } from "../stores/store.ts";
import EpgPageChangeDatePopup from "../components/popups/EpgPageChangeDatePopup.tsx";
import {
    epgPageActions,
    getEpgsForActualRange,
    getEpgsSegment1,
    getInitialEpgPage
} from "../components/epg/epgPageSlice.ts";
import EpgPageFilterPopup from "../components/popups/EpgPageFilterPopup.tsx";
import Button from "../components/buttons/Button.tsx";
import { ButtonSize, ButtonVariant } from "../types/button.ts";
import ButtonRounded from "../components/buttons/ButtonRounded.tsx";
import EpgPageXDetailsModal from "../components/test/EpgPageXDetailsModal.tsx";
import { EpgPageEpgsList } from "../components/test/EpgPageEpgsList.tsx";
import EpgPageChannelUnavailableModal from "../components/epg/EpgPageChannelUnavailableModal.tsx";
import UtilsHelper from "../helpers/utilsHelper.ts";

const NewEpgPage = () => {
    const dispatch = useAppDispatch();
    const channelsBasic = useAppSelector(store => store.epgPage.channels);

    const timelineHours = useAppSelector(state => state.epgPage.timelineHours);
    const epgSegments = useAppSelector(state => state.epgPage.epgSegments);
    const epgActualRangeFromTimestamp = useAppSelector(state => state.epgPage.epgActualRangeFromTimestamp);
    const epgActualRangeToTimestamp = useAppSelector(state => state.epgPage.epgActualRangeToTimestamp);

    const [searchInputValue, setSearchInputValue] = useState<string>("");
    const debounceSearchInputValue = useDebounce(searchInputValue, 200);
    const [isSearchMobileMode, setSearchMobileMode] = useState<boolean>(false);
    const [height] = useState(window.innerHeight);

    const { isMobile } = useContext(AppMainStateContext);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchInputValue(e.currentTarget.value);
    };

    const scrollEpgListToTimestamp = (timestamp: number) => {
        const elem = document.querySelector(`[data-timestamp="${timestamp}"]`);
        if (elem) {
            elem.scrollIntoView({ block: "end", inline: "start" });
        }
    };

    const handleSearchMobileButtonClick = () => {
        setSearchMobileMode(true);
        dispatch(epgPageActions.setIsFilterOpen(false));
        dispatch(epgPageActions.setIsChangeTimelineDatePopupOpen(false));
    }

    useEffect(() => {
        let startEpgSegment = epgSegments.find(x => x.fromTimestamp <= epgActualRangeFromTimestamp && x.toTimestamp > epgActualRangeFromTimestamp);
        let endEpgSegment = epgSegments.find(x => x.fromTimestamp <= epgActualRangeToTimestamp && x.toTimestamp > epgActualRangeToTimestamp);
        const lastEpgSegment = epgSegments?.length
            ? [...epgSegments]
                .filter(segment => segment?.fromTimestamp !== undefined) // Filtruj segmenty, które mają `fromTimestamp`
                .sort((a, b) => b.fromTimestamp - a.fromTimestamp)[0]
            : null;
        if (!startEpgSegment) {
            const firstEpgSegment = epgSegments[0];
            if (firstEpgSegment && firstEpgSegment.fromTimestamp > epgActualRangeFromTimestamp) {
                startEpgSegment = firstEpgSegment;
            }
            if (lastEpgSegment && lastEpgSegment.fromTimestamp < epgActualRangeFromTimestamp)
                startEpgSegment = lastEpgSegment;
        }
        if (!endEpgSegment) {
            if (lastEpgSegment && lastEpgSegment?.fromTimestamp < epgActualRangeToTimestamp)
                endEpgSegment = lastEpgSegment;
        }
        if (lastEpgSegment && !startEpgSegment && !endEpgSegment) {
            if (lastEpgSegment?.fromTimestamp < epgActualRangeFromTimestamp) {
                startEpgSegment = lastEpgSegment;
                endEpgSegment = lastEpgSegment;
            }
        }

        if (startEpgSegment && endEpgSegment) {
            const segmentList = epgSegments.filter(x => x.id >= startEpgSegment?.id && x.id <= endEpgSegment?.id && !x.isSegmentDownloaded);
            if (segmentList.length > 0) {
                const segmentToDownload = segmentList[0];
                if (segmentToDownload) {
                    dispatch(getEpgsSegment1({ fromTimespan: segmentToDownload.fromTimestamp }))
                }
            }
            if (segmentList.length === 0) {
                dispatch(getEpgsForActualRange({
                    fromTimestamp: epgActualRangeFromTimestamp,
                    toTimestamp: epgActualRangeToTimestamp
                }))
            }
        }
        return () => {

        }
    }, [dispatch, epgSegments, epgActualRangeFromTimestamp, epgActualRangeToTimestamp])

    useEffect(() => {
        const dateNow = new Date();
        dateNow.setHours(dateNow.getHours(), 0, 0, 0);
        const width = window.innerWidth;
        const epgWidthInHours = Math.ceil(width / import.meta.env.VITE_EPG_PAGE_TIMELINE_HOUR_WIDTH_IN_PIXELS) + 1;
        const startRangeHour = new Date(dateNow.getFullYear(), dateNow.getMonth(), dateNow.getDate(), dateNow.getHours() - Math.ceil(epgWidthInHours / 2), 0, 0, 0);
        dispatch(getInitialEpgPage({
            fromUnixTimestamp: DateHelper.DateTimeToUnixTimestamp(startRangeHour),
            hoursWidth: epgWidthInHours
        }));
        UtilsHelper.changeWindowDocumentTitle("Program TV");
    }, [dispatch]);

    useEffect(() => {
        dispatch(epgPageActions.setSearchInputValue(debounceSearchInputValue));
    }, [dispatch, debounceSearchInputValue])

    return (
        <MainLayout isFooterVisible={false} contentPadding="p-0 pt-16 md:pt-0">
            <div style={{ height: `${height-64}px` }}>

                <section className={"z-50 py-2 flex justify-between px-2 bg-neutral-800 md:sticky top-0 pt-0 md:pt-2"}>
                    {!isSearchMobileMode &&
                        <EpgPageChangeDatePopup onChange={scrollEpgListToTimestamp} />}
                    <div className={`relative flex items-center justify-end w-full sm:w-64 ${isSearchMobileMode ? "" : "space-x-3"}`}>
                        {!isSearchMobileMode &&
                            <EpgPageFilterPopup />}
                        {isMobile && !isSearchMobileMode &&
                            <ButtonRounded
                                className={"px-1 py-1 show-element-animation-ease-in-out"}
                                onClick={() => handleSearchMobileButtonClick()}
                            >
                                <MagnifyingGlassIcon className={"text-neutral-200 w-6 h-6"} />
                            </ButtonRounded>
                        }
                        {(!isMobile || (isMobile && isSearchMobileMode)) &&
                            <SearchInput
                                value={searchInputValue}
                                placeholder={"Szukaj kanału..."}
                                className={"w-full md:w-96"}
                                onInputChange={(e: React.ChangeEvent<HTMLInputElement>) => { handleInputChange(e); }}
                            />
                        }
                        {isMobile && isSearchMobileMode &&
                            <Button
                                variant={ButtonVariant.SECONDARY}
                                size={ButtonSize.BASE}
                                onClick={() => {
                                    setSearchMobileMode(false);
                                    setSearchInputValue("");
                                }}
                            >Anuluj</Button>}
                    </div>
                </section>
                <EpgPageEpgsList hourInPixel={import.meta.env.VITE_EPG_PAGE_TIMELINE_HOUR_WIDTH_IN_PIXELS} timelineHoursLength={timelineHours.length} />
                <EpgPageXDetailsModal channels={channelsBasic} />
                <EpgPageChannelUnavailableModal />
            </div>
        </MainLayout>
    );
};

export default NewEpgPage;